import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { formResolver } from '@/lib/form-resolver';
import { toast } from 'sonner';
import { useI18n } from '@/i18n';
import { 
  Button, 
  Card, 
  Tabs,
  TabsList,
  TabTrigger,
  TabContent,
  Icon,
  Skeleton,
  Spinner,
} from '@/components/ui';
import { PermissionsPanel } from '@/features/permissions/components/PermissionsPanel';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  useAgent,
  useCreateAgent,
  useUpdateAgent,
  useActivateAgent,
  useDeprecateAgent,
  useArchiveAgent,
  type CreateAgentInput,
} from '@/hooks/use-agents';
import { useCollections } from '@/hooks/use-knowledge';
import apiClient, { type PaginatedResponse } from '@/lib/api-client';
import {
  isWrappedImportedField,
  unwrapImportedField,
  wrapImportedField,
} from '@/lib/importedField';
import {
  agentCreateSchema,
  normalizeKnowledgeConfigForForm,
  type AgentCreate,
  type ModelConfig as SchemaModelConfig,
  defaultAgentFormValues,
} from '@/features/agents/schemas';
import { AgentModelsTab, PromptBudgetHint } from '@/features/agents/components';
import { isRestrictedAgent } from '@/features/agents/types';
import { AgentSkillsTab } from '@/features/skills/components/AgentSkillsTab';
import { AgentCapabilitiesTab } from '@/features/capabilities/AgentCapabilitiesTab';
import { PipelinePolicyOverrides } from '@/features/llm-config/components/PipelinePolicySections';
import { UseOnlyNotice } from '@/features/agents/components/UseOnlyNotice';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import {
  TenantKnowledgeSection,
  TenantToolsSection,
} from '@/features/agents/components/TenantAttachments';
import { useEffectiveGuardrails } from '@/hooks/use-guardrails';
import { ShieldAlert, Plus, X, ChevronDown, ChevronUp, Database, Trash2, GitBranch, Wrench, Package } from 'lucide-react';
import { useMCPServers } from '@/features/mcp/hooks/useMCPServers';
import { MCPPickerDialog } from '@/features/mcp/components/MCPPickerDialog';
import type { MCPServerSummary } from '@/features/mcp/types';
import { Collapsible } from '@vanguard-atlas/shared/src/components/ui';
import {
  PromptEditor,
  InsertReferenceDialog,
  type RefSuggestion,
  type RefDialogCollection,
  type RefDialogDocument,
} from '@vanguard-atlas/shared/src/components/ui';

// =============================================================================
// Loading Skeleton Component
// =============================================================================

function EditorSkeleton() {
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="flex gap-3">
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 w-24" />
        </div>
      </div>
      <Skeleton className="h-10 w-96 mb-6" />
      <Card className="p-6">
        <div className="space-y-6 max-w-2xl">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </Card>
    </div>
  );
}

// =============================================================================
// Agent Editor Page
// =============================================================================

export function AgentEditorPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  // REQ-AGT-009: el perfil frontera (autonomy) solo lo cambia un responsable
  // (`Module:frontier#admin`); el resto de editores lo ven en solo lectura.
  const canManageFrontier = useModuleAccess('frontier', 'admin');

  // ISS-564: el system prompt de agentes importados se muestra sin el wrapper
  // `<imported_field>`; este ref recuerda si lo tenía para re-envolverlo al
  // guardar y preservar el prompt efectivo (anti prompt-injection).
  const importedSystemWrapperRef = useRef(false);

  // Queries & Mutations
  const {
    data: existingAgent,
    isLoading: isLoadingAgent,
    error: loadError,
  } = useAgent(id ?? '', {
    enabled: isEditing && !!id,
  });

  const createMutation = useCreateAgent();
  const updateMutation = useUpdateAgent();
  const activateMutation = useActivateAgent();
  const deprecateMutation = useDeprecateAgent();
  const archiveMutation = useArchiveAgent();

  // REQ-AGT-003 (enmienda 14-Oct-2026): el MISMO editor soporta nativamente el
  // modo `use_only` — las secciones de definición del publisher se sustituyen
  // por un aviso (términos y condiciones); la configuración propia del tenant
  // (adjuntos, delegación, permisos, uso) sigue editable.
  const useOnly =
    isEditing && !!existingAgent && isRestrictedAgent(existingAgent);

  // REQ-AGT-003 (enmienda 14-Oct-2026): el MISMO editor soporta nativamente el
  // modo `use_only` — las secciones de definición del publisher se sustituyen
  // por un aviso (términos y condiciones); la configuración propia del tenant
  // (adjuntos, delegación, permisos, uso) sigue editable.

  // Effective guardrails preview (only when editing)
  const { data: effectiveGuardrails } = useEffectiveGuardrails(isEditing ? id : undefined);
  const [showInherited, setShowInherited] = useState(false);

  // Insert Reference Dialog state
  const [refDialogOpen, setRefDialogOpen] = useState(false);
  const [refDocsByCollection, setRefDocsByCollection] = useState<Record<string, RefDialogDocument[]>>({});
  const [refDocsLoading, setRefDocsLoading] = useState<Record<string, boolean>>({});

  // Collections for @ref() autocomplete and insert dialog
  const { data: allCollectionsPage } = useCollections({ page_size: 100 });
  const allCollections = allCollectionsPage?.data ?? [];

  const refSuggestions = useMemo<RefSuggestion[]>(() => {
    const items: RefSuggestion[] = [];
    for (const col of allCollections) {
      items.push({
        label: col.name,
        path: col.name,
        type: 'collection',
        icon: '📂',
      });
    }
    return items;
  }, [allCollections]);

  const refDialogCollections = useMemo<RefDialogCollection[]>(() => {
    return allCollections.map((c) => ({
      id: c.id,
      name: c.name,
      document_count: c.document_count ?? 0,
    }));
  }, [allCollections]);

  // Form setup with Zod validation
  const form = useForm<AgentCreate>({
    resolver: formResolver(agentCreateSchema),
    defaultValues: defaultAgentFormValues,
    mode: 'onBlur',
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting, isDirty },
  } = form;

  // Watch models_config for the AgentModelsTab
  const modelsConfig = watch('models_config');
  // CHG-AGT-008: soft token counter/warning for the system prompt
  const systemPromptValue = watch('prompts_config.main.system') ?? '';
  // Watch settings for the Policy tab (Sprint 278 — REQ-LLM-006 §8.1)
  const settingsValues = watch('settings');

  /**
   * Handle model change from AgentModelsTab
   * Updates the form state when a model is selected
   */
  const handleModelChange = (
    slot: 'main' | 'router' | 'tools' | 'fallback',
    config: SchemaModelConfig | null
  ) => {
    if (slot === 'main' && config) {
      // Main model is required
      setValue('models_config.main', config, { shouldDirty: true });
    } else {
      // Optional models can be null
      setValue(`models_config.${slot}`, config, { shouldDirty: true });
    }
  };

  // Load existing agent data when editing
  useEffect(() => {
    if (!existingAgent) return;
    if (isRestrictedAgent(existingAgent)) {
      // use_only (enmienda 14-Oct-2026): la definición del publisher NUNCA
      // entra al formulario — solo se hidrata el slice de uso del tenant
      // (runner allow-list + delegación operativa).
      const usage = (existingAgent.settings ?? {}) as {
        runner_policy?: { allowed_runner_ids?: string[] };
        delegation?: Record<string, unknown>;
      };
      reset({
        ...defaultAgentFormValues,
        settings: {
          ...defaultAgentFormValues.settings,
          runner_policy: {
            allowed_runner_ids: [...(usage.runner_policy?.allowed_runner_ids ?? [])],
          },
          ...(usage.delegation ? { delegation: usage.delegation } : {}),
        },
      } as AgentCreate);
      return;
    }
    {
      // Transform API response to form structure.
      // The API returns JSONB fields stored as generic dicts in the hook types,
      // but the form needs the strict typed schema. We cast through unknown
      // because the runtime data matches the backend Pydantic models.
      //
      // Normalize legacy provider values: agents created before the azure-foundry
      // migration may have 'azure-openai' or 'anthropic' — map them to 'azure-foundry'.
      const rawModels = (existingAgent.models_config as unknown as Record<string, unknown> | undefined);
      if (rawModels) {
        const LEGACY_PROVIDERS = ['azure-openai', 'anthropic', 'openai'];
        for (const slot of ['main', 'router', 'tools', 'summarizer', 'fallback']) {
          const slotConfig = rawModels[slot] as Record<string, unknown> | null | undefined;
          if (slotConfig?.provider && LEGACY_PROVIDERS.includes(slotConfig.provider as string)) {
            slotConfig.provider = 'azure-foundry';
          }
        }
      }
      // ISS-564: el system prompt se muestra sin el wrapper `<imported_field>`
      // (marca anti prompt-injection del prompt efectivo). Se recuerda si el
      // agente lo tenía para re-envolverlo al guardar (simetría).
      const rawPrompts = (existingAgent.prompts_config as unknown || defaultAgentFormValues.prompts_config) as {
        main?: { system?: string };
        guardrails?: string[];
      };
      const rawSystem = rawPrompts?.main?.system;
      importedSystemWrapperRef.current = isWrappedImportedField(rawSystem);
      reset({
        name: existingAgent.name,
        description: existingAgent.description ?? '',
        icon: 'bot',
        models_config: (rawModels as unknown || defaultAgentFormValues.models_config) as AgentCreate['models_config'],
        prompts_config: {
          ...(rawPrompts as object),
          main: { ...(rawPrompts.main ?? {}), system: unwrapImportedField(rawSystem) },
          // ISS-564 (revisión general): las guardrails importadas también
          // llevan el wrapper — se muestran desenvueltas y se re-envuelven al
          // guardar (mismo flag que el system prompt).
          ...(Array.isArray(rawPrompts.guardrails)
            ? { guardrails: rawPrompts.guardrails.map((g) => unwrapImportedField(g)) }
            : {}),
        } as AgentCreate['prompts_config'],
        knowledge_config: normalizeKnowledgeConfigForForm(existingAgent.knowledge_config),
        tools_config: (existingAgent.tools_config as unknown || defaultAgentFormValues.tools_config) as AgentCreate['tools_config'],
        settings: (existingAgent.settings as unknown || defaultAgentFormValues.settings) as AgentCreate['settings'],
      });
    }
  }, [existingAgent, reset]);

  /**
   * Transform form data to API-compatible format.
   * The form schema uses detailed types matching the backend Pydantic models,
   * while the hook types use a simplified structure for flexibility.
   * We cast through unknown because the actual data structure is compatible
   * at runtime - the type mismatch is a frontend schema versioning issue.
   */
  const transformFormDataForApi = (data: AgentCreate): CreateAgentInput => {
    // ISS-564: re-envolver el system prompt (y las guardrails) con
    // `<imported_field>` cuando el agente importado lo tenía (el editor los
    // muestra sin el wrapper).
    let promptsConfig = data.prompts_config as unknown as CreateAgentInput['prompts_config'];
    const system = (promptsConfig as { main?: { system?: unknown } } | null)?.main?.system;
    if (
      importedSystemWrapperRef.current &&
      typeof system === 'string' &&
      !isWrappedImportedField(system)
    ) {
      promptsConfig = {
        ...(promptsConfig ?? {}),
        main: { ...(promptsConfig?.main ?? {}), system: wrapImportedField(system) },
      } as CreateAgentInput['prompts_config'];
    }
    const guardrails = (promptsConfig as { guardrails?: unknown } | null)?.guardrails;
    if (importedSystemWrapperRef.current && Array.isArray(guardrails)) {
      promptsConfig = {
        ...(promptsConfig ?? {}),
        guardrails: (guardrails as unknown[]).map((g) =>
          typeof g === 'string' && !isWrappedImportedField(g) ? wrapImportedField(g) : g,
        ),
      } as CreateAgentInput['prompts_config'];
    }
    return {
      name: data.name,
      description: data.description ?? undefined,
      models_config: data.models_config as unknown as CreateAgentInput['models_config'],
      prompts_config: promptsConfig,
      knowledge_config: data.knowledge_config as unknown as CreateAgentInput['knowledge_config'],
      tools_config: data.tools_config as unknown as CreateAgentInput['tools_config'],
      settings: data.settings as unknown as CreateAgentInput['settings'],
    };
  };

  // Submit handler
  const onSubmit = async (data: AgentCreate) => {
    try {
      if (useOnly && id) {
        // use_only (enmienda 14-Oct-2026): el tenant solo escribe parámetros
        // de uso — la definición nunca viaja en el payload (el backend además
        // lo impone con 403 ante cualquier clave de definición).
        const settings = (data.settings ?? {}) as {
          runner_policy?: { allowed_runner_ids?: string[] };
          delegation?: Record<string, unknown>;
        };
        await updateMutation.mutateAsync({
          id,
          settings: {
            runner_policy: {
              allowed_runner_ids: [...(settings.runner_policy?.allowed_runner_ids ?? [])],
            },
            ...(settings.delegation ? { delegation: settings.delegation } : {}),
          },
        });
        toast.success(t('admin.agents.editor.updateSuccess') || 'Agent updated successfully');
        return;
      }
      const apiData = transformFormDataForApi(data);
      if (isEditing && id) {
        await updateMutation.mutateAsync({ id, ...apiData });
        toast.success(t('admin.agents.editor.updateSuccess') || 'Agent updated successfully');
      } else {
        const newAgent = await createMutation.mutateAsync(apiData);
        toast.success(t('admin.agents.editor.createSuccess') || 'Agent created successfully');
        navigate(`/agents/${newAgent.id}`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An error occurred';
      toast.error(isEditing 
        ? t('admin.agents.editor.updateError') || 'Failed to update agent'
        : t('admin.agents.editor.createError') || 'Failed to create agent'
      );
      console.error('Agent save error:', message);
    }
  };

  // Validation error handler - shows toast when form has errors and logs details
  const onValidationError = (fieldErrors: Record<string, unknown>) => {
    const errorFields = Object.keys(fieldErrors);
    const fieldNames = errorFields.map(f => f.replace(/_/g, ' ')).join(', ');
    console.warn('Agent form validation errors:', fieldErrors);
    toast.error(
      (t('admin.agents.editor.validationError') || 'Please fix the errors in the form before saving') +
      (errorFields.length > 0 ? ` (${fieldNames})` : '')
    );
  };

  // Status action handlers
  const handleActivate = async () => {
    if (!id) return;
    try {
      await activateMutation.mutateAsync(id);
      toast.success(t('admin.agents.actions.activateSuccess') || 'Agent activated');
    } catch {
      toast.error(t('admin.agents.actions.activateError') || 'Failed to activate agent');
    }
  };

  const handleDeprecate = async () => {
    if (!id) return;
    try {
      await deprecateMutation.mutateAsync(id);
      toast.success(t('admin.agents.actions.deprecateSuccess') || 'Agent deprecated');
    } catch {
      toast.error(t('admin.agents.actions.deprecateError') || 'Failed to deprecate agent');
    }
  };

  const handleArchive = async () => {
    if (!id) return;
    try {
      await archiveMutation.mutateAsync(id);
      toast.success(t('admin.agents.actions.archiveSuccess') || 'Agent archived');
      navigate('/agents');
    } catch {
      toast.error(t('admin.agents.actions.archiveError') || 'Failed to archive agent');
    }
  };

  // Load documents for a collection in the Insert Reference dialog
  const handleLoadDocuments = useCallback(async (collectionId: string) => {
    setRefDocsLoading((prev) => ({ ...prev, [collectionId]: true }));
    try {
      const { data: resp } = await apiClient.get<PaginatedResponse<{ id: string; filename: string; status: string }>>(
        `/api/v1/knowledge/collections/${encodeURIComponent(collectionId)}/documents`,
        { params: { page_size: 100 } },
      );
      const docs: RefDialogDocument[] = (resp.data ?? []).map((d) => ({
        id: d.id,
        filename: d.filename,
        status: d.status,
      }));
      setRefDocsByCollection((prev) => ({ ...prev, [collectionId]: docs }));
    } catch (err) {
      console.error('Failed to load documents for collection', collectionId, err);
    } finally {
      setRefDocsLoading((prev) => ({ ...prev, [collectionId]: false }));
    }
  }, []);

  // Insert @ref() into the system prompt field
  const handleRefSelect = useCallback((refPath: string) => {
    const current = form.getValues('prompts_config.main.system') ?? '';
    form.setValue('prompts_config.main.system', `${current}@ref(${refPath})`, { shouldDirty: true });
  }, [form]);

  // Show loading skeleton while loading agent data
  if (isEditing && isLoadingAgent) {
    return <EditorSkeleton />;
  }

  // Show error state
  if (loadError) {
    return (
      <div className="p-6 text-center">
        <Icon name="alert-circle" size={48} className="mx-auto mb-4 text-destructive" />
        <h2 className="text-xl font-semibold mb-2">
          {t('admin.agents.editor.loadError') || 'Failed to load agent'}
        </h2>
        <p className="text-muted-foreground mb-4">
          {loadError instanceof Error ? loadError.message : 'Unknown error'}
        </p>
        <Button variant="outline" onClick={() => navigate('/agents')}>
          {t('common.back') || 'Back to Agents'}
        </Button>
      </div>
    );
  }

  const isSaving = isSubmitting || createMutation.isPending || updateMutation.isPending;
  const agentStatus = existingAgent?.status;

  return (
    <form onSubmit={handleSubmit(onSubmit, onValidationError)} className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {isEditing ? t('admin.agents.editor.editTitle') : t('admin.agents.editor.createTitle')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t('admin.agents.editor.subtitle')}
          </p>
          {isEditing && agentStatus && (
            <div className="mt-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                agentStatus === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                agentStatus === 'deprecated' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                agentStatus === 'archived' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' :
                'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
              }`}>
                {agentStatus}
              </span>
            </div>
          )}
        </div>
        <div className="flex gap-3">
          {/* Export to CAC */}
          {existingAgent?.id && !useOnly ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/marketplace/publish')}
              title="Register this agent in the Central Artifact Catalog"
            >
              <Package className="h-4 w-4 mr-1" />
              Export to CAC
            </Button>
          ) : null}

          {/* Status Actions (no aplican en use_only: 403 fail-closed) */}
          {isEditing && !useOnly && agentStatus === 'draft' && (
            <Button
              type="button"
              variant="outline"
              onClick={handleActivate}
              disabled={activateMutation.isPending || isDirty}
            >
              {activateMutation.isPending ? <Spinner size="sm" className="mr-2" /> : null}
              {t('admin.agents.actions.activate') || 'Activate'}
            </Button>
          )}
          {isEditing && !useOnly && agentStatus === 'active' && (
            <Button
              type="button"
              variant="outline"
              onClick={handleDeprecate}
              disabled={deprecateMutation.isPending}
            >
              {deprecateMutation.isPending ? <Spinner size="sm" className="mr-2" /> : null}
              {t('admin.agents.actions.deprecate') || 'Deprecate'}
            </Button>
          )}
          {isEditing && !useOnly && (agentStatus === 'draft' || agentStatus === 'deprecated') && (
            <Button
              type="button"
              variant="outline"
              onClick={handleArchive}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending ? <Spinner size="sm" className="mr-2" /> : null}
              {t('admin.agents.actions.archive') || 'Archive'}
            </Button>
          )}
          
          <Button type="button" variant="outline" onClick={() => navigate('/agents')}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="mr-2" /> : null}
            {t('admin.agents.editor.saveDraft')}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="general">
        <TabsList className="mb-6">
          <TabTrigger value="general">{t('admin.agents.editor.tabs.general')}</TabTrigger>
          <TabTrigger value="models">{t('admin.agents.editor.tabs.models')}</TabTrigger>
          <TabTrigger value="prompts">{t('admin.agents.editor.tabs.prompts')}</TabTrigger>
          <TabTrigger value="knowledge">{t('admin.agents.editor.tabs.knowledge')}</TabTrigger>
          <TabTrigger value="tools">{t('admin.agents.editor.tabs.tools')}</TabTrigger>
          <TabTrigger value="skills">{t('admin.agents.editor.tabs.skills') || 'Skills'}</TabTrigger>
          <TabTrigger value="capabilities">{t('admin.agents.editor.tabs.capabilities') || 'Capabilities'}</TabTrigger>
          <TabTrigger value="delegation">{t('admin.agents.editor.tabs.delegation') || 'Delegation'}</TabTrigger>
          {!useOnly && (
            <TabTrigger value="policy">{t('admin.agents.editor.tabs.policy') || 'Policy'}</TabTrigger>
          )}
          <TabTrigger value="permissions">{t('admin.agents.editor.tabs.permissions') || 'Permissions'}</TabTrigger>
        </TabsList>

        {/* General Tab */}
        <TabContent value="general">
          <Card className="p-6">
            <div className="space-y-6 max-w-2xl">
              <div className="space-y-2">
                <Label htmlFor="name">
                  {t('admin.agents.editor.general.name')}
                  <span className="text-destructive ml-1">*</span>
                </Label>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="name"
                      disabled={useOnly}
                      placeholder={t('admin.agents.editor.general.namePlaceholder')}
                      className={errors.name ? 'border-destructive' : ''}
                    />
                  )}
                />
                {errors.name && (
                  <p className="text-xs text-destructive">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">
                  {t('admin.agents.editor.general.description')}
                </Label>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      id="description"
                      disabled={useOnly}
                      placeholder={t('admin.agents.editor.general.descriptionPlaceholder')}
                      rows={3}
                      value={field.value ?? ''}
                      className={errors.description ? 'border-destructive' : ''}
                    />
                  )}
                />
                {errors.description && (
                  <p className="text-xs text-destructive">{errors.description.message}</p>
                )}
              </div>
            </div>
          </Card>
        </TabContent>

        {/* Models Tab */}
        <TabContent value="models">
          {useOnly ? (
            <UseOnlyNotice section={t('admin.agents.editor.tabs.models')} />
          ) : (
            <AgentModelsTab
              modelsConfig={modelsConfig}
              onModelChange={handleModelChange}
            />
          )}
        </TabContent>

        {/* Prompts Tab */}
        <TabContent value="prompts">
          {useOnly ? (
            <UseOnlyNotice section={t('admin.agents.editor.tabs.prompts')} />
          ) : (
          <Card className="p-6">
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="systemPrompt">
                  {t('admin.agents.editor.prompts.systemPrompt')}
                  <span className="text-destructive ml-1">*</span>
                </Label>
                <Controller
                  name="prompts_config.main.system"
                  control={control}
                  render={({ field }) => (
                    <PromptEditor
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Enter the system prompt for this agent..."
                      error={!!errors.prompts_config?.main?.system}
                      highlightRefs
                      enableAutocomplete
                      suggestions={refSuggestions}
                      onInsertRef={() => setRefDialogOpen(true)}
                    />
                  )}
                />
                {errors.prompts_config?.main?.system && (
                  <p className="text-xs text-destructive">
                    {errors.prompts_config.main.system.message}
                  </p>
                )}
                <PromptBudgetHint
                  systemPrompt={systemPromptValue}
                  mainModel={modelsConfig?.main}
                />
              </div>

              {/* Agent-Level Guardrails Editor (C1) */}
              <div className="space-y-3 border-t pt-6">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldAlert className="w-4 h-4 text-orange-500" />
                  <Label>Agent Guardrails</Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Rules specific to this agent. Combined with platform and tenant guardrails at runtime.
                </p>
                <Controller
                  name="prompts_config.guardrails"
                  control={control}
                  render={({ field }) => {
                    const items: string[] = field.value ?? [];
                    const addItem = (text: string) => {
                      const trimmed = text.trim();
                      if (trimmed && !items.includes(trimmed)) {
                        field.onChange([...items, trimmed]);
                      }
                    };
                    const removeItem = (index: number) => {
                      field.onChange(items.filter((_: string, i: number) => i !== index));
                    };
                    const updateItem = (index: number, value: string) => {
                      const updated = [...items];
                      updated[index] = value;
                      field.onChange(updated);
                    };
                    return (
                      <div className="space-y-2">
                        {items.map((item: string, index: number) => (
                          <div key={index} className="flex items-start gap-2 group">
                            <span className="text-xs text-muted-foreground mt-2.5 w-5 text-right shrink-0">{index + 1}.</span>
                            <textarea
                              value={item}
                              onChange={(e) => updateItem(index, e.target.value)}
                              rows={1}
                              className="flex-1 rounded-md border bg-background px-3 py-2 text-sm resize-y min-h-[36px] focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="mt-2 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <div className="flex items-center gap-2 mt-2">
                          <span className="w-5" />
                          <input
                            type="text"
                            placeholder="Add a guardrail rule…"
                            className="flex-1 rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addItem((e.target as HTMLInputElement).value);
                                (e.target as HTMLInputElement).value = '';
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              const input = (e.currentTarget.previousElementSibling as HTMLInputElement);
                              addItem(input.value);
                              input.value = '';
                            }}
                            className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md bg-secondary text-secondary-foreground hover:bg-secondary/80"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            Add
                          </button>
                        </div>
                        {items.length === 0 && (
                          <p className="text-sm text-muted-foreground italic pl-7">
                            No agent-specific guardrails. Add rules to constrain this agent's behaviour.
                          </p>
                        )}
                      </div>
                    );
                  }}
                />
              </div>

              {/* Inherited Guardrails Preview (C4) */}
              {isEditing && effectiveGuardrails && (
                <div className="border-t pt-6">
                  <button
                    type="button"
                    onClick={() => setShowInherited(!showInherited)}
                    className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showInherited ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    Effective Guardrails Preview ({effectiveGuardrails.merged.length} total rules)
                  </button>
                  {showInherited && (
                    <div className="mt-3 space-y-3">
                      {effectiveGuardrails.platform.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-1">Platform</h4>
                          <ul className="space-y-1">
                            {effectiveGuardrails.platform.map((g, i) => (
                              <li key={`p-${i}`} className="text-sm text-muted-foreground bg-blue-50 dark:bg-blue-950/30 rounded px-3 py-1.5">{g}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {effectiveGuardrails.tenant.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider mb-1">Tenant</h4>
                          <ul className="space-y-1">
                            {effectiveGuardrails.tenant.map((g, i) => (
                              <li key={`t-${i}`} className="text-sm text-muted-foreground bg-purple-50 dark:bg-purple-950/30 rounded px-3 py-1.5">{g}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {effectiveGuardrails.agent.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wider mb-1">Agent</h4>
                          <ul className="space-y-1">
                            {effectiveGuardrails.agent.map((g, i) => (
                              <li key={`a-${i}`} className="text-sm text-muted-foreground bg-orange-50 dark:bg-orange-950/30 rounded px-3 py-1.5">{g}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {effectiveGuardrails.merged.length === 0 && (
                        <p className="text-sm text-muted-foreground italic">No guardrails configured at any level.</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
          )}
        </TabContent>

        {/* Knowledge Tab */}
        <TabContent value="knowledge">
          {useOnly && (
            <div className="mb-4">
              <UseOnlyNotice section={t('admin.agents.editor.tabs.knowledge')} />
            </div>
          )}
          {useOnly ? (
            id ? <TenantKnowledgeSection agentId={id} /> : null
          ) : (
            <AgentKnowledgeTab control={control} setValue={setValue} watch={watch} />
          )}
        </TabContent>

        {/* Tools Tab */}
        <TabContent value="tools">
          {useOnly && (
            <div className="mb-4">
              <UseOnlyNotice section={t('admin.agents.editor.tabs.tools')} />
            </div>
          )}
          {useOnly ? (
            id ? <TenantToolsSection agentId={id} /> : null
          ) : (
            <AgentToolsTab control={control} setValue={setValue} watch={watch} />
          )}
        </TabContent>

        {/* Skills Tab (ISS-563: muestra la herencia del hive cuando aplica) */}
        <TabContent value="skills">
          {useOnly && (
            <div className="mb-4">
              <UseOnlyNotice section={t('admin.agents.editor.tabs.skills')} />
            </div>
          )}
          <AgentSkillsTab agentId={id} hiveId={existingAgent?.hive_id} hideScope={useOnly} />
        </TabContent>

        {/* Capabilities Tab (Sprint 267 — REQ-CAP-002 §4: herencia tenant→hive→agente) */}
        <TabContent value="capabilities">
          <AgentCapabilitiesTab agentId={id} agent={existingAgent} />
        </TabContent>

        {/* Delegation Tab */}
        <TabContent value="delegation">
          <Card className="p-6">
            <div className="space-y-6 max-w-2xl">
              <div className="flex items-center gap-3 mb-2">
                <GitBranch className="w-5 h-5 text-purple-500" />
                <h3 className="text-lg font-semibold">
                  {t('admin.agents.editor.delegation.title') || 'Delegation Settings'}
                </h3>
              </div>
              <p className="text-sm text-muted-foreground">
                {t('admin.agents.editor.delegation.description') || 'Configure how this agent participates in multi-agent orchestration via hives and handoffs.'}
              </p>

              {/* Enable delegation outbound */}
              <Controller
                name="settings.delegation.enabled"
                control={control}
                render={({ field }) => (
                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                    <input
                      type="checkbox"
                      checked={field.value ?? false}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className="rounded border w-4 h-4"
                    />
                    <div>
                      <span className="text-sm font-medium">
                        {t('admin.agents.editor.delegation.enabled') || 'Can initiate delegations'}
                      </span>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t('admin.agents.editor.delegation.enabledHelp') || 'Allow this agent to delegate tasks to other agents via handoffs.'}
                      </p>
                    </div>
                  </label>
                )}
              />

              {/* Enable delegation inbound */}
              <Controller
                name="settings.delegation.can_be_delegated_to"
                control={control}
                render={({ field }) => (
                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                    <input
                      type="checkbox"
                      checked={field.value ?? false}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className="rounded border w-4 h-4"
                    />
                    <div>
                      <span className="text-sm font-medium">
                        {t('admin.agents.editor.delegation.canBeDelegated') || 'Can receive delegations'}
                      </span>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t('admin.agents.editor.delegation.canBeDelegatedHelp') || 'Allow other agents to send tasks to this agent.'}
                      </p>
                    </div>
                  </label>
                )}
              />

              {/* Numeric settings — shown when delegation is enabled */}
              <div className="grid grid-cols-3 gap-4 pt-2">
                <div className="space-y-1">
                  <Label className="text-xs">
                    {t('admin.agents.editor.delegation.maxDepth') || 'Max Depth'}
                  </Label>
                  <Controller
                    name="settings.delegation.max_depth"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="number"
                        min={1}
                        max={5}
                        {...field}
                        value={field.value ?? 3}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                      />
                    )}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('admin.agents.editor.delegation.maxDepthHelp') || 'Max nested delegation depth (1-5)'}
                  </p>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">
                    {t('admin.agents.editor.delegation.maxTokens') || 'Max Response Tokens'}
                  </Label>
                  <Controller
                    name="settings.delegation.max_response_tokens"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="number"
                        min={256}
                        max={16384}
                        step={256}
                        {...field}
                        value={field.value ?? 4096}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                      />
                    )}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('admin.agents.editor.delegation.maxTokensHelp') || 'Token limit for delegation responses (256-16384)'}
                  </p>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">
                    {t('admin.agents.editor.delegation.maxFanOut') || 'Max Fan-out'}
                  </Label>
                  <Controller
                    name="settings.delegation.max_fan_out"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="number"
                        min={1}
                        max={10}
                        {...field}
                        value={field.value ?? 5}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                      />
                    )}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('admin.agents.editor.delegation.maxFanOutHelp') || 'Max concurrent parallel delegations (1-10)'}
                  </p>
                </div>
              </div>

              {/* Delegation Prompt */}
              <div className="space-y-2">
                <Label>
                  {t('admin.agents.editor.delegation.prompt') || 'Delegation Prompt'}
                </Label>
                <Controller
                  name="settings.delegation.delegation_prompt"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value || null)}
                      placeholder={t('admin.agents.editor.delegation.promptPlaceholder') || 'Additional instructions for when this agent acts as a delegatee...'}
                      rows={4}
                      className="resize-none"
                    />
                  )}
                />
                <p className="text-xs text-muted-foreground">
                  {t('admin.agents.editor.delegation.promptHelp') || 'Optional instructions appended to the system prompt when this agent receives a delegation. Max 2000 characters.'}
                </p>
              </div>

              {/* Runner policy allowlist */}
              <div className="space-y-2 border-t pt-4">
                <Label>
                  Runner Allowlist (IDs)
                </Label>
                <Controller
                  name="settings.runner_policy.allowed_runner_ids"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      value={Array.isArray(field.value) ? field.value.join(', ') : ''}
                      onChange={(e) => {
                        const ids = e.target.value
                          .split(',')
                          .map((item) => item.trim())
                          .filter(Boolean);
                        field.onChange(ids);
                      }}
                      placeholder="agent_01ABC..., agent_01XYZ..."
                      rows={3}
                      className="resize-none"
                    />
                  )}
                />
                <p className="text-xs text-muted-foreground">
                  If empty, any available runner can be used. If set, chat tool execution is limited to these runner IDs.
                </p>
              </div>
            </div>
          </Card>
        </TabContent>

        {/* Policy Tab — Sprint 278 (ROUTE-PIPE-1/A — REQ-LLM-006 §8.1) */}
        {!useOnly && (
          <TabContent value="policy">
            <Card className="p-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-2">
                  <GitBranch className="w-5 h-5 text-indigo-500" />
                  <h3 className="text-lg font-semibold">
                    {t('admin.agents.editor.policy.title') || 'Pipeline Policy'}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Overrides del agente para la política del pipeline
                  (routing · optimización · loops · tiempos).
                  Los campos en «Inherit» se resuelven con las capas
                  hive → tenant → DR; el override del agente gana.
                </p>
                <PipelinePolicyOverrides
                  settings={
                    (settingsValues as unknown as Record<string, unknown>) ?? {}
                  }
                  onChange={(next) =>
                    setValue('settings', next as AgentCreate['settings'], {
                      shouldDirty: true,
                    })
                  }
                  responsibleLock={
                    canManageFrontier
                      ? undefined
                      : {
                          fields: [
                            'frontier_agents_enabled',
                            'frontier_on_unavailable',
                            'frontier_max_duration_minutes',
                          ],
                          hint:
                            t('admin.agents.editor.policy.frontierResponsible') ||
                            'Solo un responsable frontera puede cambiar el perfil autónomo.',
                        }
                  }
                />
              </div>
            </Card>
          </TabContent>
        )}

        {/* Permissions Tab */}
        <TabContent value="permissions">
          <Card className="p-6">
            {id ? (
              <PermissionsPanel entityType="agent" entityId={id} />
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {t('admin.agents.editor.permissions.saveFirst') || 'Save the agent first to manage permissions.'}
              </div>
            )}
          </Card>
        </TabContent>
      </Tabs>

      {/* Insert Reference Dialog for @ref() */}
      <InsertReferenceDialog
        open={refDialogOpen}
        onOpenChange={setRefDialogOpen}
        collections={refDialogCollections}
        onLoadDocuments={handleLoadDocuments}
        documentsByCollection={refDocsByCollection}
        loadingCollections={refDocsLoading}
        onSelect={handleRefSelect}
      />
    </form>
  );
}

// =============================================================================
// Agent Knowledge Tab — Collection Picker
// =============================================================================

interface AgentKnowledgeTabProps {
  control: ReturnType<typeof useForm<AgentCreate>>['control'];
  setValue: ReturnType<typeof useForm<AgentCreate>>['setValue'];
  watch: ReturnType<typeof useForm<AgentCreate>>['watch'];
}

export function AgentKnowledgeTab({ control, setValue, watch }: AgentKnowledgeTabProps) {
  const { t } = useI18n();
  const [showPicker, setShowPicker] = useState(false);

  // Fetch available collections from API
  const { data: collectionsPage, isLoading: isLoadingCollections } = useCollections({ page_size: 100 });
  const availableCollections = collectionsPage?.data ?? [];

  // Watch current collections in form
  const knowledgeConfig = watch('knowledge_config');
  const selectedCollections = knowledgeConfig?.collections ?? [];

  const addCollection = (name: string) => {
    const alreadyAdded = selectedCollections.some((c) => c.name === name);
    if (alreadyAdded) return;
    setValue(
      'knowledge_config.collections',
      [...selectedCollections, { name, priority: 1, filters: {} }],
      { shouldDirty: true }
    );
  };

  const removeCollection = (index: number) => {
    setValue(
      'knowledge_config.collections',
      selectedCollections.filter((_, i) => i !== index),
      { shouldDirty: true }
    );
  };

  // Collections not yet assigned to this agent
  const unassignedCollections = availableCollections.filter(
    (c) => !selectedCollections.some((sc) => sc.name === c.name || sc.name === c.id)
  );

  return (
    <Card className="p-6">
      <div className="space-y-6">
        {/* Section header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {t('admin.agents.editor.knowledge.title') || 'Knowledge Collections'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {t('admin.agents.editor.knowledge.description') || 'Select knowledge collections this agent can access for RAG.'}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowPicker(!showPicker)}
            disabled={isLoadingCollections}
          >
            <Plus className="w-4 h-4 mr-1" />
            {t('admin.agents.editor.knowledge.addCollection') || 'Add Collection'}
          </Button>
        </div>

        {/* Collection picker dropdown */}
        {showPicker && (
          <div className="border rounded-lg p-4 bg-muted/30">
            <p className="text-sm font-medium mb-2">
              {t('admin.agents.editor.knowledge.availableCollections') || 'Available Collections'}
            </p>
            {isLoadingCollections ? (
              <p className="text-sm text-muted-foreground">Loading collections...</p>
            ) : unassignedCollections.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                {availableCollections.length === 0
                  ? (t('admin.agents.editor.knowledge.noCollectionsExist') || 'No collections exist yet. Create collections in the Knowledge section first.')
                  : (t('admin.agents.editor.knowledge.allCollectionsAdded') || 'All available collections are already added.')}
              </p>
            ) : (
              <div className="grid gap-2 max-h-48 overflow-y-auto">
                {unassignedCollections.map((col) => (
                  <button
                    key={col.id}
                    type="button"
                    className="flex items-center justify-between w-full text-left px-3 py-2 rounded-md border bg-background hover:bg-muted transition-colors"
                    onClick={() => {
                      addCollection(col.name);
                      setShowPicker(false);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium text-sm">{col.name}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {col.document_count} docs • {col.total_chunks} chunks
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Selected collections list */}
        {selectedCollections.length > 0 ? (
          <div className="space-y-2">
            {selectedCollections.map((col, index) => {
              // Find matching collection from API for metadata
              const match = availableCollections.find((c) => c.name === col.name || c.id === col.name);
              return (
                <div
                  key={col.name}
                  className="flex items-center justify-between px-4 py-3 rounded-lg border bg-card group"
                >
                  <div className="flex items-center gap-3">
                    <Database className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium text-sm">{col.name}</p>
                      {match && (
                        <p className="text-xs text-muted-foreground">
                          {match.document_count} documents • {match.total_chunks} chunks
                          {match.status !== 'active' && (
                            <span className="ml-2 text-yellow-600">({match.status})</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-muted-foreground flex items-center gap-1">
                      Priority:
                      <Controller
                        name={`knowledge_config.collections.${index}.priority`}
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            min={1}
                            max={10}
                            {...field}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                            className="w-12 text-center rounded border bg-background px-1 py-0.5 text-xs"
                          />
                        )}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => removeCollection(index)}
                      className="text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity p-1"
                      title="Remove collection"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-10 text-muted-foreground">
            <Database className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">
              {t('admin.agents.editor.knowledge.emptyState') || 'No knowledge collections assigned. Add collections to enable RAG for this agent.'}
            </p>
          </div>
        )}

        {/* Retrieval Settings — advanced block, collapsed by default (CHG-AGT-001) */}
        <div className="border-t pt-6">
          <Collapsible
            testId="agent-knowledge-advanced"
            trigger={
              <span className="text-sm font-medium">
                {t('admin.agents.editor.knowledge.advancedSettings') ||
                  'Advanced settings'}
              </span>
            }
            contentClassName="pt-4"
          >
            <h4 className="text-sm font-medium mb-3">
              {t('admin.agents.editor.knowledge.retrievalSettings') || 'Retrieval Settings'}
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 max-w-xl">
              <div className="space-y-1">
                <Label className="text-xs">
                  {t('admin.agents.editor.knowledge.topK') || 'Top K'}
                </Label>
                <Controller
                  name="knowledge_config.retrieval.top_k"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="number"
                      min={1}
                      max={100}
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    />
                  )}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">
                  {t('admin.agents.editor.knowledge.scoreThreshold') ||
                    'Score Threshold'}
                </Label>
                <Controller
                  name="knowledge_config.retrieval.score_threshold"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="number"
                      min={0}
                      max={1}
                      step={0.05}
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    />
                  )}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">
                  {t('admin.agents.editor.knowledge.enableReranking') || 'Rerank'}
                </Label>
                <Controller
                  name="knowledge_config.retrieval.rerank_enabled"
                  control={control}
                  render={({ field }) => (
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(field.value)}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="rounded border"
                      />
                      <span className="text-sm">
                        {t('admin.agents.editor.knowledge.enabled') || 'Enabled'}
                      </span>
                    </label>
                  )}
                />
              </div>
            </div>
          </Collapsible>
        </div>
      </div>
    </Card>
  );
}

// =============================================================================
// Agent Tools Tab — MCP Server Picker
// =============================================================================

interface AgentToolsTabProps {
  control: ReturnType<typeof useForm<AgentCreate>>['control'];
  setValue: ReturnType<typeof useForm<AgentCreate>>['setValue'];
  watch: ReturnType<typeof useForm<AgentCreate>>['watch'];
}

export function AgentToolsTab({ setValue, watch }: AgentToolsTabProps) {
  const { t } = useI18n();
  const [pickerOpen, setPickerOpen] = useState(false);

  // Lookup map for already-bound servers (name/status/tool count). The picker
  // itself paginates in the dialog; this keeps labels resolvable without
  // showing internal ids in the selected list.
  const { data: serversPage } = useMCPServers({ pageSize: 100 });
  const availableServers = serversPage?.data ?? [];

  // Watch current tools_config in form
  const toolsConfig = watch('tools_config') ?? [];

  const isServerBound = useCallback(
    (tcServer: string, server: MCPServerSummary) => {
      // Backward compatibility: older agents may have stored server_type instead of id.
      return tcServer === server.id || tcServer === server.server_type;
    },
    [],
  );

  const addServer = (server: MCPServerSummary) => {
    const alreadyAdded = toolsConfig.some((tc) => isServerBound(tc.server, server));
    if (alreadyAdded) return;
    setValue(
      'tools_config',
      [...toolsConfig, { server: server.id, operations: [] }],
      { shouldDirty: true }
    );
  };

  const removeServer = (index: number) => {
    setValue(
      'tools_config',
      toolsConfig.filter((_, i) => i !== index),
      { shouldDirty: true }
    );
  };

  return (
    <Card className="p-6">
      <div className="space-y-6">
        {/* Section header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {t('admin.agents.editor.tools.title') || 'MCP Tools'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {t('admin.agents.editor.tools.description') || 'Select MCP servers this agent can use as tools.'}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPickerOpen(true)}
          >
            <Plus className="w-4 h-4 mr-1" />
            {t('admin.agents.editor.tools.addTool') || 'Add Tools'}
          </Button>
        </div>

        {pickerOpen && (
          <MCPPickerDialog
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            isBound={(server) =>
              toolsConfig.some((tc) => isServerBound(tc.server, server))
            }
            onAdd={addServer}
          />
        )}

        {/* Selected servers list */}
        {toolsConfig.length > 0 ? (
          <div className="space-y-2">
            {toolsConfig.map((tc, index) => {
              // Find matching server from API for metadata
              const match = availableServers.find((s) => isServerBound(tc.server, s));
              return (
                <div
                  key={tc.server}
                  className="flex items-center justify-between px-4 py-3 rounded-lg border bg-card group"
                >
                  <div className="flex items-center gap-3">
                    <Wrench className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium text-sm">
                        {match?.name ?? t('admin.agents.editor.tools.unknownServer')}
                      </p>
                      {match && (
                        <p className="text-xs text-muted-foreground">
                          {t('admin.agents.editor.tools.toolsCount', {
                            count: match.tools_count,
                          })}
                          {match.status !== 'active' && (
                            <span className="ml-2 text-amber-600 dark:text-amber-400">
                              {t(
                                `admin.agents.editor.tools.serverStatus.${match.status}`,
                              )}
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeServer(index)}
                    className="text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity p-1"
                    title={t('admin.agents.editor.tools.removeServer')}
                    aria-label={t('admin.agents.editor.tools.removeServer')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-10 text-muted-foreground">
            <Wrench className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">
              {t('admin.agents.editor.tools.emptyState') || 'No tools configured. Add MCP servers to enable tool use for this agent.'}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}
